import React, { useMemo } from 'react';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useAuthStore } from '../../store';
import TicketsView from './tickets-view';
import useURLSearchQuery from '../../hooks/use-url-for-search-query';
import useTicketsSearch from '../../hooks/use-tickets-search';
import useTicketListActions from '../../hooks/use-ticket-list-actions';
import {
  Option,
  AssigneeOption,
  TicketsDefaultSearch,
  defaultSearch,
  TYPE_FILTER_OPTIONS,
  formatTeamMembersAsOptions,
  teamMemberSearchRequest,
} from './options';

const ALL_ASSIGNEE: AssigneeOption = { title: 'All', value: '' };

const TicketsContainer = () => {
  const apiClient = useApiClient();
  const { loggedInTeamMember } = useAuthStore();

  const [queryValues, updateURLQuery] = useURLSearchQuery<TicketsDefaultSearch>(defaultSearch);
  const {
    search, type, assignee, sort, status, severity,
  } = queryValues;

  const {
    tickets, totalHits, isLoading, error, hasMore, loadMore, refetch,
  } = useTicketsSearch({
    search, type, assignee, sort, status, severity,
  });

  const isSeverityChipsDisabled = !status.includes('CLOSED');

  // Team members for assignee filter
  const { data: teamMembers } = useFetch<SearchResult<TeamMember>>(
    apiClient.peopleClient.postTeamMemberSearch,
    [teamMemberSearchRequest],
    true,
  );

  const assignees = useMemo<AssigneeOption[]>(() => {
    const options = formatTeamMembersAsOptions(teamMembers, [ALL_ASSIGNEE]);
    return options;
  }, [teamMembers]);

  // Derive selected filter values from URL state
  const selectedTypes = useMemo<Option[]>(
    () => TYPE_FILTER_OPTIONS.filter((o) => type.includes(o.value)),
    [type],
  );

  const selectedAssignee = useMemo<AssigneeOption>(
    () => assignees.find((a) => a.value === assignee) || ALL_ASSIGNEE,
    [assignee, assignees],
  );

  // Filter handlers
  const onSearchChange = (value: string) => updateURLQuery({ search: value });
  const onTypeChange = (_: any, values: Option[]) => updateURLQuery({ type: values.map((v) => v.value) });
  const onAssigneeChange = (a: AssigneeOption | null) => updateURLQuery({ assignee: a?.value || '' });
  const onSortChange = (s: string) => updateURLQuery({ sort: s });
  const onStatusChipChange = (chips: string[]) => {
    const clearSeverity = !chips.includes('CLOSED');
    updateURLQuery({
      status: chips,
      severity: clearSeverity ? [] : severity,
    });
  };
  const onSeverityChipChange = (chips: string[]) => updateURLQuery({ severity: chips });

  const {
    isModalOpen,
    editingTicket,
    handleOpenCreate,
    handleEditTicket,
    handleCloseModal,
    handleSaveTicket,
    isCloseModalOpen,
    handleCloseCloseModal,
    handleSaveClose,
    isReassignModalOpen,
    reassigningTicketAssigneeIds,
    handleReassignTicket,
    handleCloseReassignModal,
    handleSaveReassign,
    isDeleteModalOpen,
    handleDeleteTicket,
    handleCloseDeleteModal,
    handleConfirmDelete,
    handleStatusChange,
  } = useTicketListActions(refetch);

  return (
    <TicketsView
      // Data
      tickets={tickets}
      totalHits={totalHits}
      isLoading={isLoading}
      error={error || undefined}
      hasMore={hasMore}
      loadMore={loadMore}
      // Filters
      searchValue={search}
      onSearchChange={onSearchChange}
      selectedTypes={selectedTypes}
      onTypeChange={onTypeChange}
      assignees={assignees}
      selectedAssignee={selectedAssignee}
      onAssigneeChange={onAssigneeChange}
      sortValue={sort}
      onSortChange={onSortChange}
      selectedStatusChips={status}
      onStatusChipChange={onStatusChipChange}
      selectedSeverityChips={severity}
      onSeverityChipChange={onSeverityChipChange}
      isSeverityChipsDisabled={isSeverityChipsDisabled}
      // Actions
      onOpenCreate={handleOpenCreate}
      onEditTicket={handleEditTicket}
      onReassignTicket={handleReassignTicket}
      onDeleteTicket={handleDeleteTicket}
      onStatusChange={handleStatusChange}
      // Modal
      isModalOpen={isModalOpen}
      editingTicket={editingTicket}
      defaultAssigneeIds={loggedInTeamMember?.id ? [loggedInTeamMember.id] : []}
      onCloseModal={handleCloseModal}
      onSaveTicket={handleSaveTicket}
      // Close modal
      isCloseModalOpen={isCloseModalOpen}
      onCloseCloseModal={handleCloseCloseModal}
      onSaveClose={handleSaveClose}
      // Reassign modal
      isReassignModalOpen={isReassignModalOpen}
      reassigningTicketAssigneeIds={reassigningTicketAssigneeIds}
      onCloseReassignModal={handleCloseReassignModal}
      onSaveReassign={handleSaveReassign}
      // Delete modal
      isDeleteModalOpen={isDeleteModalOpen}
      onCloseDeleteModal={handleCloseDeleteModal}
      onConfirmDelete={handleConfirmDelete}
    />
  );
};

export default TicketsContainer;
